import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';

interface FreeLifetimePromoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyPromo?: (code: string) => void;
  showInstructions?: boolean;
}

export function FreeLifetimePromoModal({
  isOpen,
  onClose,
  onApplyPromo,
  showInstructions = true,
}: FreeLifetimePromoModalProps) {
  const [copied, setCopied] = useState(false);

  // Fetch the promo code from the backend
  const { data: promoData, isLoading } = useQuery({
    queryKey: ['lifetime-promo'],
    queryFn: async () => {
      const response = await apiClient.get('/onboarding/lifetime-promo');
      return response.data;
    },
    enabled: isOpen, // Only fetch when modal is open
  });

  const promoCode = promoData?.code || '';
  const isEnabled = promoData?.enabled ?? true;

  // If offer is disabled on backend, close modal and don't show it
  useEffect(() => {
    if (isOpen && !isLoading && !isEnabled) {
      onClose();
    }
  }, [isOpen, isLoading, isEnabled, onClose]);

  if (!isOpen) return null;

  const handleCopy = () => {
    if (!promoCode) return;
    navigator.clipboard.writeText(promoCode);
    setCopied(true);
    toast.success('Promo code copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApply = () => {
    if (onApplyPromo) {
      onApplyPromo(promoCode);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Content */}
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Free Lifetime Access!
          </h3>
          <p className="text-gray-600">
            Use this special promo code to get lifetime free access to Flouri. No credit card required!
          </p>
        </div>

        {/* Promo Code Display */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-500 rounded-lg p-6 mb-6">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">Your Promo Code</p>
            {isLoading ? (
              <div className="text-xl font-bold text-gray-600 my-4">Loading...</div>
            ) : (
              <>
                <div className="text-3xl font-bold text-gray-900 tracking-wider mb-4">
                  {promoCode}
                </div>
                <button
                  onClick={handleCopy}
                  disabled={!promoCode}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {copied ? (
                    <>
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      Copy Code
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Instructions */}
        {showInstructions && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-900 mb-2">How to use:</h4>
            <ol className="text-sm text-blue-800 space-y-1 ml-5 list-decimal">
              <li>Copy the promo code above</li>
              <li>Enter it in the "Promo Code" field below</li>
              <li>Click "Validate" to activate</li>
              <li>Complete signup - no credit card needed!</li>
            </ol>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {onApplyPromo && (
            <button
              onClick={handleApply}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
            >
              Apply Code
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
          >
            {onApplyPromo ? 'Close' : 'Got It!'}
          </button>
        </div>
      </div>
    </div>
  );
}
